import React from 'react';
import {
  SearchInput,
  SearchDetails,
  Facets,
  FilterList,
  SortOn,
  ViewSwitcher,
} from '@plone/volto/components/manage/Blocks/Search/components';
import { Grid, Segment } from 'semantic-ui-react';
import { Button } from 'semantic-ui-react';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { resolveExtension } from '@plone/volto/helpers/Extensions/withBlockExtensions';
import config from '@plone/volto/registry';

import AdvancedPanel from '../../../../../../components/Search/AdvancedPanel';
import SearchControls from '../../../../../../components/Search/SearchControls';
import SearchCorrection from '../../../../../../components/Search/SearchCorrection';
import useSearchControls from '../../../../../../helpers/useSearchControls';
import useSearchMeta from '../../../../../../helpers/useSearchMeta';
import useMediaQuery, {
  STACKABLE,
} from '../../../../../../helpers/useMediaQuery';
import { SITE_CHOICES } from '../../../../../../config/searchRegistry.generated';
import useSiteConfig from '../../../../../../helpers/useSiteConfig';
import { facetPresetRoutes } from '../../../../../../presets';
import {
  facetRouteTarget,
  presetIdFrom,
  presetSwitchHref,
  singleFacetValue,
} from '../../../../../../components/Browse/browseState';

/**
 * SHADOW of @plone/volto LeftColumnFacets (the `facetsLeftSide` search-block
 * layout).
 *
 * Two changes vs core:
 *
 * 1. Render <SearchControls> between the active-filter chips and the result
 *    count, for blocks that declare `mode: 'search'` (the search app). That is
 *    where the status scope, the match operator and the "applied by default"
 *    chips live — see components/Search/SearchControls.
 *
 *    It has to be HERE rather than inside the results table because these
 *    controls change the query, not the presentation: they belong with the
 *    search box and the facets, above the count, so the count visibly answers
 *    them. Browse pages render the identical layout — they declare no mode, so
 *    the strip never mounts.
 *
 * 2. Below Semantic's `stackable` breakpoint the facet rail becomes a DRAWER
 *    (item 17). Stacked, the rail is a screen and a half of filter widgets
 *    sitting between the visitor and the first result: measured on standards
 *    at 500px wide, the rail ran 565px tall and pushed the results table to
 *    y=1014 in a 760px-tall viewport. Nobody scrolls past that to discover
 *    there were results underneath.
 *
 *    The drawer chrome — toggle, close, footer, scrim — is in the DOM at every
 *    width and hidden by CSS above the breakpoint, so the desktop layout is
 *    unchanged apart from those hidden nodes. See theme/search.less.
 *
 * 3. A facet value that has a page of its own NAVIGATES to it (2026-09-27).
 *    On directives, each of the 40 series is a `?preset=series-...` landing
 *    page, and the Series facet used to filter in place instead: on a series
 *    page it offered only that series and its clear did nothing, and on All
 *    Documents a pick produced a `query=` URL nobody links to. Now picking a
 *    series goes to that series' page and clearing it goes back to All
 *    Documents, with the visitor's other facet selections carried across. The
 *    decision is the pure `facetRouteTarget` (browseState.js); both Facets
 *    and FilterList's chips and "Clear all" pass through `applyFacets` below,
 *    so all three agree. Why a facet and not the old 40-chip wall: one control
 *    for one choice (NN/g, duplicate links), type-ahead for a known value out
 *    of 40 (Home Office long-lists pattern, W3C APG combobox).
 *
 * The sibling `../components` import is rewritten to the absolute `@plone/volto`
 * path (Volto customization rule), which is also what makes the addon's own
 * SearchInput / SortOn shadows resolve here.
 */
const messages = defineMessages({
  searchButtonText: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  filters: {
    id: 'Filters',
    defaultMessage: 'Filters',
  },
  closeFilters: {
    id: 'Close filters',
    defaultMessage: 'Close filters',
  },
  showResults: {
    id: 'Show {count} results',
    defaultMessage: 'Show {count} results',
  },
});

const FilterGlyph = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" fill="none">
    <path
      d="M3 5h14M6 10h8M9 15h2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CloseGlyph = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" fill="none">
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const FacetWrapper = ({ children, facetSettings = {}, visible }) => {
  const { advanced, field = {} } = facetSettings;

  return (
    <Segment
      basic
      // item 28: the "More filters" toggle needs a real id list to point
      // aria-controls at -- only advanced facets ever hide, so only they
      // need one.
      id={advanced && field.value ? `advanced-facet-${field.value}` : undefined}
      className={cx('facet', {
        [`facet-index-${field.value}`]: !!field.value,
        'advanced-facet': advanced,
        'advanced-facet-hidden': !visible,
      })}
    >
      {children}
    </Segment>
  );
};

const ALL_SITES = SITE_CHOICES.map((s) => s.value);

/**
 * The visitor's own facet selections, as query rows, to carry across a
 * facet-driven preset switch (header, change 3). The routed index is left out,
 * since the target page sets it, and so is any value equal to the block's base
 * row on that index: that belongs to the page, not to the visitor.
 */
const carriedFacetRows = (data, facets, routedIndex) => {
  const { types } = config.blocks.blocksConfig.search.extensions.facetWidgets;
  const base = Object.assign(
    {},
    ...(data.query?.query || []).map(({ i, v }) => ({ [i]: v })),
  );
  return (data.facets || [])
    .map((facet) => {
      const field = facet?.field?.value;
      if (!field || field === routedIndex) return null;
      const value = facets?.[field];
      if (singleFacetValue(value) === null) return null;
      if (
        field in base &&
        JSON.stringify(singleFacetValue(base[field])) ===
          JSON.stringify(singleFacetValue(value))
      ) {
        return null;
      }
      const { valueToQuery } = resolveExtension('type', types, facet);
      return valueToQuery ? valueToQuery({ value, facet }) : null;
    })
    .filter(Boolean);
};

/**
 * How many of the DRAWER'S OWN facets are narrowing the result set — the number
 * on the closed toggle, which has to agree with the chip count in the
 * "Filters:" row FilterList renders above the results.
 *
 * Counting every value in `facets` does not agree with it. `facets` also
 * carries the block's own base constraints — on the browse pages,
 * `portal_type: ['Standard']` — which no widget in the drawer can change and
 * FilterList does not count. Measured: one ticked checkbox, FilterList said 1,
 * a naive count said 2. Only the fields the block configures as facets are
 * eligible.
 */
const countActiveFacets = (facets, fields) =>
  fields.reduce((n, field) => {
    const value = facets?.[field];
    if (Array.isArray(value)) return n + value.length;
    // The date-range facet holds an object from the first render, so its mere
    // presence means nothing — it counts once it has a bound.
    if (value && typeof value === 'object') {
      return n + (Object.values(value).some(Boolean) ? 1 : 0);
    }
    return n + (value ? 1 : 0);
  }, 0);

const LeftColumnFacets = (props) => {
  const {
    children,
    data,
    totalItems,
    facets,
    setFacets,
    setSortOn,
    setSortOrder,
    sortOn,
    sortOrder,
    onTriggerSearch,
    searchedText, // search text for previous search
    searchText, // search text currently being entered (controlled input)
    setSearchText,
    isEditMode,
    querystring = {},
    id,
    // searchData,
    // mode = 'view',
    // variation,
  } = props;
  const { showSearchButton } = data;
  const isLive = !showSearchButton;
  const intl = useIntl();

  const showControls = data.mode === 'search' && !isEditMode;
  const { meta } = useSearchMeta(id);
  const [, setControls] = useSearchControls();

  const hasFacets = data.facets?.length > 0;
  const facetsId = `${id}-facets`;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const isDrawer = useMediaQuery(STACKABLE);
  // The drawer declares role="dialog" aria-modal="true", so it has to deliver
  // dialog focus semantics: focus moves in on open, stays in while open, and
  // returns to the toggle on close — whichever of the four close paths ran.
  const toggleRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false);
    toggleRef.current?.focus();
  }, []);
  const facetFields = React.useMemo(
    () =>
      (data.facets || [])
        .filter((f) => !f.hidden)
        .map((f) => f.field?.value)
        .filter(Boolean),
    [data.facets],
  );
  const activeFacets = countActiveFacets(facets, facetFields);

  // Change 3 in the header: facet values with a page of their own navigate.
  const site = useSiteConfig();
  const location = useLocation();
  const history = useHistory();
  const facetRoutes = React.useMemo(
    () => facetPresetRoutes(site?.id, data.family),
    [site?.id, data.family],
  );
  const applyFacets = (f) => {
    const target =
      !isEditMode &&
      facetRouteTarget({
        routes: facetRoutes,
        activePreset: presetIdFrom(location?.search),
        current: facets,
        next: f,
      });
    if (target) {
      history.push(
        presetSwitchHref({
          pathname: location.pathname,
          search: location.search,
          presetId: target.presetId,
          // Facet rows are rebuilt from `rows`; base rows belong to the page
          // being left, and the target page brings its own.
          dropIndexes: [
            ...(data.facets || []).map((facet) => facet?.field?.value),
            ...(data.query?.query || []).map(({ i }) => i),
          ].filter(Boolean),
          rows: carriedFacetRows(data, f, target.index),
        }),
      );
      return;
    }
    flushSync(() => {
      setFacets(f);
      onTriggerSearch(searchedText || '', f);
    });
  };

  // Widening past the breakpoint has to reset the state, not just re-style it:
  // the rail becomes an ordinary column again, and an `open` left behind would
  // hold the scroll lock on a page with no scrim and nothing to close.
  React.useEffect(() => {
    if (!isDrawer && drawerOpen) setDrawerOpen(false);
  }, [isDrawer, drawerOpen]);

  // Focus enters the dialog when it opens (the close button is the first
  // control a keyboard user needs), per the ARIA dialog pattern.
  React.useEffect(() => {
    if (isDrawer && drawerOpen) {
      dialogRef.current?.querySelector('.pp-facets-close')?.focus();
    }
  }, [isDrawer, drawerOpen]);

  React.useEffect(() => {
    if (!isDrawer || !drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      // aria-modal promises the background is unreachable; the scroll lock
      // alone does not deliver that for the keyboard, so trap Tab inside.
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const tabbables = [
        ...root.querySelectorAll(
          "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
      ].filter((el) => el.offsetParent !== null);
      if (!tabbables.length) return;
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!root.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // The drawer scrolls itself; without this the scrim scrolls the results
    // behind it, which reads as the page having lost the drawer.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawer, drawerOpen]);

  return (
    <Grid className="searchBlock-facets left-column-facets" stackable>
      {data.headline && (
        <Grid.Row>
          <Grid.Column>
            <h2 className="headline">{data.headline}</h2>
          </Grid.Column>
        </Grid.Row>
      )}

      <Grid.Row>
        {hasFacets && (
          <Grid.Column
            className="pp-facets-col"
            mobile={12}
            tablet={4}
            computer={3}
          >
            {drawerOpen && (
              // The scrim is a redundant target — Escape closes the drawer and
              // so does its own labelled button — so it stays a plain div
              // rather than announcing a control that adds nothing.
              <div
                className="pp-facets-scrim"
                onClick={closeDrawer}
                aria-hidden="true"
              />
            )}
            <div
              id={facetsId}
              ref={dialogRef}
              className={cx('facets', { 'is-open': drawerOpen })}
              {...(isDrawer && drawerOpen
                ? {
                    role: 'dialog',
                    'aria-modal': 'true',
                    'aria-label': intl.formatMessage(messages.filters),
                  }
                : {})}
            >
              {/* Drawer-only chrome. Present at every width and hidden by CSS
                  above the breakpoint — rendering it conditionally would mean
                  reading the viewport during render, which the server cannot
                  do (see helpers/useMediaQuery). */}
              <div className="pp-facets-bar">
                <span className="pp-facets-bar__title">
                  {intl.formatMessage(messages.filters)}
                </span>
                <button
                  type="button"
                  className="pp-facets-close"
                  onClick={closeDrawer}
                  aria-label={intl.formatMessage(messages.closeFilters)}
                >
                  <CloseGlyph />
                </button>
              </div>

              {/* Facet titles are h3s (see the facet component shadows); this
                  gives them a parent level so the page's heading outline has
                  no h1-to-h3 skip. Visually the rail explains itself. */}
              <h2 className="prc-visually-hidden">Filter results</h2>
              {data.facetsTitle && <h3>{data.facetsTitle}</h3>}
              <Facets
                querystring={querystring}
                data={data}
                facets={facets}
                setFacets={applyFacets}
                facetRoutes={facetRoutes}
                facetWrapper={FacetWrapper}
              />

              {/* Every facet applies immediately, so this is a "done" button,
                  not an "apply" one — it states what closing will show, which
                  is the only question a visitor has while filtering. */}
              <div className="pp-facets-actions">
                <button
                  type="button"
                  className="pp-facets-done"
                  onClick={closeDrawer}
                >
                  {intl.formatMessage(messages.showResults, {
                    count: (totalItems ?? 0).toLocaleString(),
                  })}
                </button>
              </div>
            </div>
          </Grid.Column>
        )}

        <Grid.Column
          mobile={12}
          tablet={data.facets?.length ? 8 : 12}
          computer={data.facets?.length ? 9 : 12}
        >
          {/* First thing in the results column, so that once the rail is out
              of flow this is the first thing on the page. */}
          {hasFacets && (
            <button
              type="button"
              ref={toggleRef}
              className="pp-facets-toggle"
              aria-expanded={drawerOpen}
              aria-controls={facetsId}
              onClick={() => setDrawerOpen(true)}
            >
              <FilterGlyph />
              {intl.formatMessage(messages.filters)}
              {activeFacets > 0 && (
                <span className="pp-facets-toggle__n">{activeFacets}</span>
              )}
            </button>
          )}

          {(Object.keys(data).includes('showSearchInput')
            ? data.showSearchInput
            : true) && (
            <div className="search-wrapper">
              <SearchInput {...props} isLive={isLive} />
              {data.showSearchButton && (
                <Button primary onClick={() => onTriggerSearch(searchText)}>
                  {data.searchButtonLabel ||
                    intl.formatMessage(messages.searchButtonText)}
                </Button>
              )}
            </div>
          )}

          {/* Advanced search (item 4). Directly under the box because it
              COMPOSES what goes in the box — see the component header. Search
              app only: browse pages have no free-text box for it to write to. */}
          {showControls && (
            <AdvancedPanel
              id={id}
              searchText={searchText}
              setSearchText={setSearchText}
              onTriggerSearch={(text) => onTriggerSearch(text, facets)}
            />
          )}

          <FilterList
            {...props}
            isEditMode={isEditMode}
            setFacets={applyFacets}
          />

          {showControls && (
            <SearchControls
              meta={meta}
              onChange={setControls}
              // Widening the scope is a FACET change, not a control param: the
              // Archive facet is the one true home of site scope, so removing
              // the "this archive only" default ticks every box in it. The
              // chip and the left-rail facet then agree by construction.
              onClearScope={() => {
                const next = { ...facets, site: ALL_SITES };
                flushSync(() => {
                  setFacets(next);
                  onTriggerSearch(searchedText || '', next);
                });
              }}
            />
          )}

          {/* item 21. Above the count, because it changes what the count
              is counting — the number below is for the CORRECTED query. */}
          <SearchCorrection block={id} />

          <div className="search-results-count-sort">
            {/* item 21: the corrected text when there was one. `searchedText`
                is the block's own copy of what was TYPED, so leaving it here
                made this line say "Searched for: qualifcation" directly under
                "Showing results for qualification" — two labels for one result
                set, disagreeing about what produced it. */}
            {/* as="p": this line is a result summary, not a section heading —
                as core's default h4 it was the page's only h4 and tripped
                heading-order. role="status" makes count changes audible to
                screen readers (facet clicks were previously silent). */}
            <div role="status">
              <SearchDetails
                as="p"
                text={meta?.corrected || searchedText}
                total={totalItems}
                data={data}
              />
            </div>
            <div className="sort-views-wrapper">
              {data.showSortOn && (
                <SortOn
                  querystring={querystring}
                  data={data}
                  isEditMode={isEditMode}
                  sortOn={sortOn}
                  sortOrder={sortOrder}
                  setSortOn={(sortOn) => {
                    flushSync(() => {
                      setSortOn(sortOn);
                      onTriggerSearch(searchedText || '', facets, sortOn);
                    });
                  }}
                  setSortOrder={(sortOrder) => {
                    flushSync(() => {
                      setSortOrder(sortOrder);
                      onTriggerSearch(
                        searchedText || '',
                        facets,
                        sortOn,
                        sortOrder,
                      );
                    });
                  }}
                />
              )}
              {data.availableViews && data.availableViews.length > 1 && (
                <ViewSwitcher {...props} />
              )}
            </div>
          </div>
          {children}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default LeftColumnFacets;
